import React, { useState, Fragment, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, DocumentArrowUpIcon, PhotoIcon, BuildingLibraryIcon } from '@heroicons/react/24/outline';
import { api, getTenantSlug } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

interface BookUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadSuccess: () => void;
  isAdmin?: boolean;
}

const BookUploadModal: React.FC<BookUploadModalProps> = ({ isOpen, onClose, onUploadSuccess, isAdmin = false }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuth();

  // Detect "institute admin uploading on their tenant subdomain" — cues a
  // contextual callout that the book will be tagged to the institute and
  // becomes available for group-assignment after platform review. Without
  // this hint, an institute principal sees the generic "review queue" copy
  // and wonders why their own institute's book needs platform approval.
  const tenantSlug = getTenantSlug();
  const isInstituteAdminHere = Boolean(
    tenantSlug && user?.institution_memberships?.some(
      m => m.institution_slug === tenantSlug
        && (m.role === 'institution_admin' || m.role === 'institution_sub_admin')
    )
  );
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    category: '',
    isbn: '',
    description: '',
    // Phase 2 multilingual: declared source language of the PDF. Drives the
    // translate-before-extract step in the ingestion pipeline and is checked
    // by the upload-time PDF quality probe (rejects legacy Kruti-Dev fonts
    // that look Indic on screen but extract as Latin garbage).
    language: 'english',
  });
  const [bookFile, setBookFile] = useState<File | null>(null);
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState<'uploading' | 'processing' | ''>('');
  const [categories, setCategories] = useState<string[]>([]);

  // Fetch categories from API when modal opens. All roles see the metadata
  // form now, so the dropdown is needed regardless of is_admin.
  useEffect(() => {
    if (isOpen) {
      fetchCategories();
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/books/categories');
      const fetchedCategories = response.data.filter((cat: string) => cat !== 'all');
      setCategories(fetchedCategories.length > 0
        ? fetchedCategories
        : ['Mathematics', 'Physics', 'Chemistry', 'Computer Science', 'Psychology', 'Economics', 'Biology']);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setCategories(['Mathematics', 'Physics', 'Chemistry', 'Computer Science', 'Psychology', 'Economics', 'Biology']);
    }
  };

  const handleClose = () => {
    setUploadProgress(0);
    setError('');
    setUploadStage('');
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!bookFile) {
      setError('Please select a book file');
      return;
    }

    setUploading(true);
    setError('');
    setUploadStage('uploading');

    // Every role fills the same form. Backend treats metadata as optional
    // for non-admins (defaults title to filename stem when blank) and
    // admins must provide title+author (server-side validation enforces it).
    const uploadData = new FormData();
    uploadData.append('book_file', bookFile);
    uploadData.append('title', formData.title);
    uploadData.append('author', formData.author);
    uploadData.append('category', formData.category);
    uploadData.append('isbn', formData.isbn);
    uploadData.append('description', formData.description);
    uploadData.append('language', formData.language);
    if (coverImage) {
      uploadData.append('cover_image', coverImage);
    }

    try {
      await api.post('/api/books/upload', uploadData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
            if (percentCompleted === 100) {
              setUploadStage('processing');
            }
          }
        },
      });

      // Reset form
      setFormData({
        title: '',
        author: '',
        category: '',
        isbn: '',
        description: '',
        language: 'english',
      });
      setBookFile(null);
      setCoverImage(null);
      setUploadProgress(0);
      setUploadStage('');

      onUploadSuccess();
      handleClose();

      if (isAdmin) {
        showToast('success', 'Book uploaded', 'Available in the library now.');
      } else {
        showToast(
          'success',
          'Upload received',
          'An admin will review it shortly. Taking you to My Requests to track status...',
        );
        setTimeout(() => navigate('/my-requests'), 1500);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to upload book');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadStage('');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'book' | 'cover') => {
    const file = e.target.files?.[0];
    if (file) {
      if (type === 'book') {
        if (!file.name.toLowerCase().endsWith('.pdf')) {
          setError('Only PDF files are supported');
          return;
        }
        setBookFile(file);
      } else {
        const validImageTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!validImageTypes.includes(file.type)) {
          setError('Cover image must be JPG, PNG, or WebP');
          return;
        }
        setCoverImage(file);
      }
      setError('');
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
                <form onSubmit={handleSubmit}>
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <Dialog.Title as="h3" className="text-xl font-semibold leading-6 text-gray-900">
                        {isAdmin ? 'Upload Textbook' : isInstituteAdminHere ? 'Upload for your Institute' : 'Upload a Book'}
                      </Dialog.Title>
                      <button
                        type="button"
                        className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                        onClick={handleClose}
                      >
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>

                    {isInstituteAdminHere && !isAdmin && (
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex gap-3">
                        <BuildingLibraryIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-900">
                          <p className="font-medium">Tagged to your institute on submit.</p>
                          <p className="text-blue-800 mt-1">
                            Our team reviews uploaded books before they're available to assign.
                            Once approved, you'll see this book in the &ldquo;Assign Book&rdquo; picker on any group page.
                          </p>
                        </div>
                      </div>
                    )}

                    {error && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                        {error}
                      </div>
                    )}

                    {uploading && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">
                            {uploadStage === 'uploading' ? 'Uploading to server' : 'Processing & storing file'}
                          </span>
                          <span className="text-sm text-gray-600">
                            {uploadStage === 'processing' ? 'Processing...' : `${uploadProgress}%`}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div
                            className={`h-2.5 rounded-full transition-all duration-300 ease-out ${
                              uploadStage === 'processing'
                                ? 'bg-green-600 animate-pulse'
                                : 'bg-blue-600'
                            }`}
                            style={{
                              width: uploadStage === 'processing' ? '100%' : `${uploadProgress}%`
                            }}
                          />
                        </div>
                        <p className="text-sm text-gray-600 mt-2">
                          {uploadStage === 'uploading' && uploadProgress < 100 && 'Uploading your file to the server...'}
                          {uploadStage === 'processing' && (
                            <span className="text-green-600">
                              Upload complete! Processing file (extracting pages, storing to {process.env.REACT_APP_STORAGE_BACKEND || 'cloud'}...)
                            </span>
                          )}
                        </p>
                      </div>
                    )}

                    <div className="space-y-4">
                      {/* Book File Upload */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Book File (PDF) *
                        </label>
                        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-gray-400">
                          <div className="space-y-1 text-center">
                            <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <div className="flex text-sm text-gray-600">
                              <label
                                htmlFor="book-upload"
                                className="relative cursor-pointer rounded-md bg-white font-medium text-blue-600 hover:text-blue-500"
                              >
                                <span>Upload a file</span>
                                <input
                                  id="book-upload"
                                  name="book-upload"
                                  type="file"
                                  className="sr-only"
                                  accept=".pdf"
                                  onChange={(e) => handleFileChange(e, 'book')}
                                />
                              </label>
                              <p className="pl-1">or drag and drop</p>
                            </div>
                            <p className="text-xs text-gray-500">PDF up to 500MB</p>
                            {bookFile && (
                              <p className="text-sm text-green-600 mt-2">
                                Selected: {bookFile.name}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {!isAdmin && (
                        <p className="text-sm text-gray-500">
                          These details help our reviewers approve your book faster — but every field below is optional, so feel free to leave anything blank and our team will fill it in during review.
                        </p>
                      )}

                      {/* Title — required for platform admins (server enforces); optional for everyone else */}
                      <div>
                        <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                          Title{isAdmin ? ' *' : ''}
                        </label>
                        <input
                          type="text"
                          id="title"
                          required={isAdmin}
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>

                      {/* Author */}
                      <div>
                        <label htmlFor="author" className="block text-sm font-medium text-gray-700">
                          Author{isAdmin ? ' *' : ''}
                        </label>
                        <input
                          type="text"
                          id="author"
                          required={isAdmin}
                          value={formData.author}
                          onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>

                      {/* Category */}
                      <div>
                        <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                          Category
                        </label>
                        <div className="mt-1 flex gap-2">
                          <input
                            type="text"
                            id="category"
                            list="category-options"
                            value={formData.category}
                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                            placeholder="Select or type a category"
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                          <datalist id="category-options">
                            {categories.map((cat) => (
                              <option key={cat} value={cat} />
                            ))}
                          </datalist>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          Select from existing categories or type a new one
                        </p>
                      </div>

                      {/* Language — Phase 2 multilingual */}
                      <div>
                        <label htmlFor="book-language" className="block text-sm font-medium text-gray-700">
                          Book language
                        </label>
                        <select
                          id="book-language"
                          value={formData.language}
                          onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white"
                        >
                          <option value="english">English</option>
                          <option value="hindi">हिन्दी (Hindi)</option>
                          <option value="gujarati">ગુજરાતી (Gujarati)</option>
                          <option value="marathi">मराठी (Marathi)</option>
                        </select>
                        <p className="mt-1 text-xs text-gray-500">
                          What language is the PDF written in? We translate Indic books to English
                          internally for retrieval; your reading experience stays in your preferred language.
                        </p>
                      </div>

                      {/* ISBN — always optional */}
                      <div>
                        <label htmlFor="isbn" className="block text-sm font-medium text-gray-700">
                          ISBN
                        </label>
                        <input
                          type="text"
                          id="isbn"
                          value={formData.isbn}
                          onChange={(e) => setFormData({ ...formData, isbn: e.target.value })}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>

                      {/* Description */}
                      <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                          Description
                        </label>
                        <textarea
                          id="description"
                          rows={3}
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>

                      {/* Cover Image Upload */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Cover Image (Optional)
                        </label>
                        <div className="mt-1 flex items-center space-x-4">
                          <div className="flex-shrink-0">
                            <PhotoIcon className="h-12 w-12 text-gray-400" />
                          </div>
                          <div className="flex-1">
                            <label
                              htmlFor="cover-upload"
                              className="cursor-pointer rounded-md bg-white font-medium text-blue-600 hover:text-blue-500"
                            >
                              <span>Upload cover image</span>
                              <input
                                id="cover-upload"
                                name="cover-upload"
                                type="file"
                                className="sr-only"
                                accept="image/*"
                                onChange={(e) => handleFileChange(e, 'cover')}
                              />
                            </label>
                            {coverImage && (
                              <p className="text-sm text-green-600 mt-1">
                                Selected: {coverImage.name}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      type="submit"
                      disabled={uploading}
                      className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {uploading ? (
                        <div className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {uploadStage === 'uploading'
                            ? `Uploading... ${uploadProgress}%`
                            : 'Processing...'
                          }
                        </div>
                      ) : (
                        'Upload Book'
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={uploading}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default BookUploadModal;
